import { useEffect, useMemo, useRef, useState } from "react";
import LEAD_FORM_STEPS, { LEAD_STEP, FIN_STEP, PROPERTY_STEP, CUSTOMER_STEP } from "../data/leadFormSteps";
import { validateFields, buildFieldPayload } from "../utils/formSchema";
import { isApiSuccess } from "../utils/apiResponse";
import { saveApplicationDraft, getApplicationDraft, clearApplicationDraft } from "../api/session";
import {
  fetchConfig,
  normalizeConfigResponse,
  fetchPartnerList,
  financierDisplayName,
} from "../api/configApi";
import {
  createProspect,
  addFinancier,
  getLeadDetails,
  saveLeadData,
  saveLeadDataBeacon,
  extractProspectId,
  formatLoanCode,
} from "../api/leadApi";
import { fetchStateCityAll, normalizeStateCity } from "../api/commonServiceApi";

const PROPERTY_FIELDS = [...PROPERTY_STEP.blocks[0].fields, ...PROPERTY_STEP.blocks[1].fields];
const CUSTOMER_MAIN_FIELDS = [
  ...CUSTOMER_STEP.blocks[0].fields,
  ...CUSTOMER_STEP.blocks[1].fields,
  ...CUSTOMER_STEP.blocks[2].fields,
  ...CUSTOMER_STEP.blocks[3].fields,
];
const V_FIELDS = [...LEAD_STEP.blocks[0].fields, ...FIN_STEP.blocks[0].fields, ...PROPERTY_FIELDS, ...CUSTOMER_MAIN_FIELDS];
const CO_BLOCKS = CUSTOMER_STEP.blocks[4].blocks;
const CO_FIELDS = CO_BLOCKS.flatMap((b) => b.fields);
// Borrower personal / address / employment — the collapsible sections
// (blocks[3] is the always-visible "has co-borrower" flag).
const CUSTOMER_ACCORDION_BLOCKS = CUSTOMER_STEP.blocks.slice(0, 3);

// Total financiers one lead can have, across step 2 and "Add bank" on the
// Send to Bank page (not the "up to 2" the design mockup hardcoded).
// Real backend cap (rem-backend's MAX_LEAD_FINANCIERS) — no President/BH
// approval is needed for any of them.
export const MAX_FINANCIERS = 5;

// last_form_filled comes back as the same step "key" the JSON schema itself
// uses (e.g. "property_loan_details") — map it to a step number dynamically
// instead of hardcoding a parallel list that could drift out of sync.
const STEP_NUMBER_BY_KEY = Object.fromEntries(LEAD_FORM_STEPS.map((s, i) => [s.key, i + 1]));

// How long the user can go untouched on steps 4/5 before autosave quietly
// persists the draft (save_type: "partial") without them hitting Next/Submit.
const AUTOSAVE_IDLE_MS = 20000;

const defaultValues = () => {
  const v = {};
  V_FIELDS.forEach((f) => {
    if (f.name) v[f.name] = f.value ?? "";
  });
  return v;
};

// save_lead_data's customer_coborrower_details is nested (personal_details/
// address/employment), but v/each coborrowers entry is flat — these mirror
// the DTO's own field groupings (rem-backend save-lead-data.dto.ts) so the
// payload lands in the right sub-object. Field names don't collide across
// groups, so a straight key-list pick is enough; no need to introspect the
// JSON schema's own blocks.
const PERSONAL_DETAIL_KEYS = [
  "date_of_birth",
  "birthplace_city_id",
  "gender",
  "mother_name",
  "govt_id_type",
  "secondary_govt_id_type",
  "govt_id_number",
  "nationality",
  "marital_status",
  "spouse_name",
];
const ADDRESS_KEYS = [
  "pa_address",
  "pa_province",
  "pa_city",
  "pa_zipcode",
  "pa_residence_type",
  "same_as_permanent",
  "cra_address_1",
  "cra_address_2",
  "cra_province",
  "cra_city",
  "cra_zipcode",
  "cra_residence_type",
];
const EMPLOYMENT_KEYS = [
  "employment_type",
  "source_of_income",
  "employer_name",
  "company_address",
  "company_telephone_no",
  "job_position",
  "total_monthly_income_gross",
  "years_with_company",
];
const GOV_ID_KEYS = ["govt_id_type", "secondary_govt_id_type"];
// alternate_phone_number is a co-borrower-only field — the borrower form has no
// input for it, so a stale value loaded from get_lead_details (e.g. "09…")
// must not be sent back for the borrower, where the user can't see or fix it.
const COBORROWER_PERSONAL_KEYS = [...PERSONAL_DETAIL_KEYS, "alternate_phone_number", "name", "phone_number", "relationship_with_borrower"];

// save_lead_data rejects empty keys, so blank values (undefined, null, "" or
// only spaces) are left out of the request entirely instead of being sent.
const isBlank = (val) => val == null || (typeof val === "string" && val.trim() === "");
// Dropdown values are held as strings ("3"), but save_lead_data validates the
// id-backed ones as integers (e.g. govt_id_type) — so every dropdown / state /
// city field on the customer step goes out as a number when it's all digits.
// Typed-in digits (mobile, telephone, zip) stay strings.
const ID_FIELD_TYPES = ["dd", "sdd", "state", "city"];
const collectIdFields = (node, out = new Set()) => {
  if (Array.isArray(node)) node.forEach((n) => collectIdFields(n, out));
  else if (node && typeof node === "object") {
    if (node.name && ID_FIELD_TYPES.includes(node.type)) out.add(node.name);
    Object.values(node).forEach((n) => collectIdFields(n, out));
  }
  return out;
};
const CUSTOMER_ID_FIELDS = collectIdFields(CUSTOMER_STEP);
const toApiValue = (k, val) => (CUSTOMER_ID_FIELDS.has(k) && /^\d+$/.test(String(val)) ? Number(val) : val);

const pick = (source, keys) => {
  const out = {};
  keys.forEach((k) => {
    if (!isBlank(source[k])) out[k] = toApiValue(k, source[k]);
  });
  return out;
};

// One co-borrower per entry in the coborrowers array — save_lead_data's
// co_borrower_details is an array too, one item per co-borrower, so this maps
// 1:1 without needing to invent a wrapping/grouping scheme. coborrower_id (set
// once a saved co-borrower is loaded back via flattenCustomerDetails) is
// echoed back so a re-save updates the same record instead of duplicating it.
const buildCustomerCoborrowerDetails = (v, coborrowers) => {
  const details = {
    ...pick(v, ["has_coborrower", "coborrower_count"]),
    personal_details: pick(v, PERSONAL_DETAIL_KEYS),
    address: pick(v, ADDRESS_KEYS),
    employment: pick(v, EMPLOYMENT_KEYS),
  };
  if (v.has_coborrower === "yes" && coborrowers.length) {
    details.co_borrower_details = coborrowers.map((cb) => ({
      ...(cb.coborrower_id != null ? { coborrower_id: cb.coborrower_id } : {}),
      personal_details: pick(cb, COBORROWER_PERSONAL_KEYS),
      address: pick(cb, ADDRESS_KEYS),
      employment: pick(cb, EMPLOYMENT_KEYS),
    }));
  }
  return details;
};

// Keeps the coborrowers array exactly coborrower_count entries long —
// padding with blanks when it grows, trimming from the end when it shrinks.
const resizeCoborrowers = (list, count) => {
  const n = Math.max(0, Number(count) || 0);
  const next = list.slice(0, n);
  while (next.length < n) next.push({});
  return next;
};

// Inverse of buildCustomerCoborrowerDetails — get_lead_details' customer_details
// tab comes back nested the same way (personal_details/address/employment,
// plus co_borrower_details as an array); flatten it back onto v/coborrowers.
// null/undefined values are dropped rather than overwriting existing form
// state with blanks.
const flattenSection = (obj) => {
  const out = {};
  Object.keys(obj || {}).forEach((k) => {
    if (obj[k] != null) out[k] = obj[k];
  });
  return out;
};
// Dropdown ids from the lead row come back as 0 when never saved.
const isSavedId = (val) => val != null && Number(val) !== 0;
// customer_details' employment_type falls back to the lead row's (0 until
// step 2 is saved) — drop that so it doesn't overwrite step 2's blank value.
const flattenEmployment = (obj) => {
  const out = flattenSection(obj);
  if (!isSavedId(out.employment_type)) delete out.employment_type;
  return out;
};
const flattenCustomerDetails = (tab) => {
  if (!tab) return { v: {}, coborrowers: [] };
  const vPatch = {
    ...flattenSection({ has_coborrower: tab.has_coborrower, coborrower_count: tab.coborrower_count }),
    ...flattenSection(tab.personal_details),
    ...flattenSection(tab.address),
    ...flattenEmployment(tab.employment),
  };
  const coborrowers = Array.isArray(tab.co_borrower_details)
    ? tab.co_borrower_details.map((cb) => ({
        ...(cb?.coborrower_id != null ? { coborrower_id: cb.coborrower_id } : {}),
        ...flattenSection(cb?.personal_details),
        ...flattenSection(cb?.address),
        ...flattenEmployment(cb?.employment),
      }))
    : [];
  return { v: vPatch, coborrowers };
};

// The 5-step lead/application form: lead details, financier & bank
// selection, (documents are their own hook), property/loan details and
// customer/co-borrower details — plus everything needed to create, resume
// (Complete Application) and survive a refresh on that flow.
export default function useApplicationForm({ navigate, location, applicationLeadMatch, flash, touchSaved, setSavedAt }) {
  const [step, setStep] = useState(1);
  const [v, setV] = useState(defaultValues());
  const [coborrowers, setCoborrowers] = useState([]);
  const [configOptions, setConfigOptions] = useState({});
  const configLoadedRef = useRef(false);
  const stateCityLoadedRef = useRef(false);
  const [savingLead, setSavingLead] = useState(false);
  const [savingFinancier, setSavingFinancier] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [loadingLeadDetails, setLoadingLeadDetails] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [coFieldErrors, setCoFieldErrors] = useState([]);
  const [fins, setFins] = useState({});
  // True once add_financier has already persisted a selection for this lead —
  // step 2 becomes read-only (just "Next") the same way step 1 does once loanId
  // exists, instead of letting the user silently re-edit without re-saving.
  const [financierSaved, setFinancierSaved] = useState(false);
  // "Save financiers?" confirm before step 2's add_financier — once saved the
  // selection can't be changed on this page, so the user verifies it first.
  const [finConfirm, setFinConfirm] = useState(false);
  // Per-lead flags from the dashboard listing (get_leads), threaded through
  // continueLead — get_lead_details doesn't expose these, so they default to
  // the safe/permissive assumption when reached any other way (a brand-new
  // lead created in this session, or a direct URL hit with no dashboard
  // context to source them from).
  const [canAddFinancier, setCanAddFinancier] = useState(true);
  const [isFrozen, setIsFrozen] = useState(false);
  // Upload Documents stays editable after the application is created (even
  // when the rest of the form is view-only) until the lead freezes on contract
  // signing — only a frozen lead, or someone else's, locks documents.
  const [docsLocked, setDocsLocked] = useState(false);
  // Every tab openable regardless of maxStepReached — only for a view-only
  // lead whose application is already created (all tabs' data exists). A
  // view-only lead still mid-creation (the dashboard's "View" on someone
  // else's incomplete lead) keeps tabs past last_form_filled locked.
  const [allStepsOpen, setAllStepsOpen] = useState(false);
  // The furthest step this lead has actually reached — the Stepper only lets
  // the user jump to the active step or an earlier (already-submitted) one,
  // never ahead to a step with no data yet. isFrozen (view-only, or a lead
  // that's fully created) bypasses this entirely — see goStep.
  const [maxStepReached, setMaxStepReached] = useState(1);
  const bumpMaxStep = (n) => setMaxStepReached((m) => Math.max(m, n));
  // The step whose save (create_prospect / add_financier / save_lead_data)
  // last succeeded — a refresh lands back on it, the same way continueLead
  // lands on the dashboard's last_form_filled.
  const [lastSavedStep, setLastSavedStep] = useState(null);
  // The persist effect below is declared before the restore effect, so on
  // mount it would otherwise overwrite the stored draft with the blank initial
  // state before the restore effect gets a chance to read it. State (not a
  // ref) so it only flips once the restored values have actually committed —
  // StrictMode's double-run of mount effects would otherwise still clobber it.
  const [draftRestored, setDraftRestored] = useState(false);
  const [loanId, setLoanId] = useState(null);
  const [leadModal, setLeadModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [openCust, setOpenCust] = useState(null);
  const [openAcc, setOpenAcc] = useState({ property_details: true, loan_details: true });
  // Keyed by co-borrower index — { [index]: openBlockKey }.
  const [openCb, setOpenCb] = useState({});
  const copyTimer = useRef(null);

  const onApplicationRoute = location.pathname === "/application" || !!applicationLeadMatch;

  // Autosave (steps 4/5 only — the two form_types save_lead_data actually
  // knows about; lead_details/financier selection go through their own
  // create_prospect/add_financier calls with no "partial" concept). Always
  // read through this ref rather than closing over step/loanId/v/coborrowers
  // directly, so the rarely-recreated listeners below never fire with stale
  // data from whenever they happened to last be (re)attached.
  const latestRef = useRef({});
  useEffect(() => {
    latestRef.current = { step, loanId, v, coborrowers, isFrozen };
  });

  const autosaveNow = () => {
    const { step: s, loanId: lid, v: vals, coborrowers: cobs, isFrozen: frozen } = latestRef.current;
    if (!lid || frozen) return;
    if (s === STEP_NUMBER_BY_KEY.property_loan_details) {
      saveLeadDataBeacon(lid, "property_loan_details", buildFieldPayload(PROPERTY_FIELDS, vals));
    } else if (s === STEP_NUMBER_BY_KEY.customer_coborrower_details) {
      saveLeadDataBeacon(lid, "customer_coborrower_details", buildCustomerCoborrowerDetails(vals, cobs));
    }
  };

  // Tab switched away/minimized, browser (or the whole app) loses focus, or
  // the tab is actually being closed — any of these should persist whatever's
  // been typed on steps 4/5 without waiting for the idle-autosave timer below.
  useEffect(() => {
    const markSaved = () => {
      if (location.pathname === "/application") touchSaved();
    };
    const onVisibilityChange = () => {
      markSaved();
      if (document.visibilityState === "hidden") autosaveNow();
    };
    const onBlur = () => {
      markSaved();
      autosaveNow();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onBlur);
    window.addEventListener("pagehide", autosaveNow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("pagehide", autosaveNow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Idle autosave: if the user stops typing on step 4/5 for a while without
  // hitting Next/Submit, quietly persist the draft (save_type: "partial") so
  // it isn't only sitting in memory/localStorage.
  useEffect(() => {
    if (!onApplicationRoute || !loanId || isFrozen) return;
    if (step !== STEP_NUMBER_BY_KEY.property_loan_details && step !== STEP_NUMBER_BY_KEY.customer_coborrower_details) return;
    const timer = setTimeout(() => {
      const payload =
        step === STEP_NUMBER_BY_KEY.property_loan_details
          ? { formType: "property_loan_details", details: buildFieldPayload(PROPERTY_FIELDS, v) }
          : { formType: "customer_coborrower_details", details: buildCustomerCoborrowerDetails(v, coborrowers) };
      saveLeadData(loanId, payload.formType, payload.details, "partial")
        .then(() => touchSaved())
        .catch(() => {
          /* best-effort — the next idle tick, or a hide/close, will retry */
        });
    }, AUTOSAVE_IDLE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onApplicationRoute, loanId, isFrozen, step, v, coborrowers]);

  // Keep the whole in-progress form (lead id, step, every field) persisted so a
  // browser refresh on /application restores it exactly — including edits to
  // steps that have no save API yet, which otherwise only live in memory.
  useEffect(() => {
    if (!onApplicationRoute || !draftRestored) return;
    saveApplicationDraft({ loanId, step, v, coborrowers, fins, financierSaved, canAddFinancier, isFrozen, docsLocked, allStepsOpen, maxStepReached, lastSavedStep });
  }, [onApplicationRoute, loanId, step, v, coborrowers, fins, financierSaved, canAddFinancier, isFrozen, docsLocked, allStepsOpen, maxStepReached, lastSavedStep, draftRestored]);

  // Primary/secondary government ID types aren't user-editable here (the
  // schema ships them is_enable: false) — the backend sets them from the gov-ID
  // documents tagged on Upload Documents. That can happen after the lead's
  // details were first loaded (or the form was restored from the local draft
  // without refetching), so re-read just these keys from get_lead_details'
  // personal_details every time Customer Details opens.
  useEffect(() => {
    if (!onApplicationRoute || !loanId || step !== STEP_NUMBER_BY_KEY.customer_coborrower_details) return;
    let cancelled = false;
    getLeadDetails(loanId, ["customer_details"])
      .then(({ data }) => {
        if (cancelled || !isApiSuccess(data)) return;
        const tab = data.data?.customer_details;
        if (!tab) return;
        const govIds = (personal) => flattenSection(pick(personal || {}, GOV_ID_KEYS));
        const borrower = govIds(tab.personal_details);
        if (Object.keys(borrower).length) setV((prev) => ({ ...prev, ...borrower }));
        if (Array.isArray(tab.co_borrower_details)) {
          setCoborrowers((prev) =>
            prev.map((cb, i) => {
              const patch = govIds(tab.co_borrower_details[i]?.personal_details);
              return Object.keys(patch).length ? { ...cb, ...patch } : cb;
            })
          );
        }
      })
      .catch((err) => {
        console.error("gov ID type refresh failed", err.response?.status, err.response?.data || err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [onApplicationRoute, loanId, step]);

  // Keeps coborrowers exactly coborrower_count entries long as the user
  // changes that dropdown. Left alone while has_coborrower !== "yes" so
  // toggling it off and back on with the same count doesn't lose data.
  useEffect(() => {
    if (v.has_coborrower !== "yes") return;
    const count = Number(v.coborrower_count) || 0;
    setCoborrowers((prev) => (prev.length === count ? prev : resizeCoborrowers(prev, count)));
  }, [v.has_coborrower, v.coborrower_count]);

  const loadConfigIfNeeded = () => {
    if (!configLoadedRef.current) {
      fetchConfig()
        .then(({ data }) => {
          if (!isApiSuccess(data)) {
            console.error("config fetch failed", data?.message);
            return;
          }
          configLoadedRef.current = true;
          setConfigOptions((prev) => ({ ...prev, ...normalizeConfigResponse(data) }));
        })
        .catch((err) => {
          console.error("config fetch failed", err.response?.status, err.response?.data || err.message);
        });
    }

    if (!stateCityLoadedRef.current) {
      fetchStateCityAll()
        .then(({ data }) => {
          if (!isApiSuccess(data)) {
            console.error("state/city fetch failed", data?.message);
            return;
          }
          stateCityLoadedRef.current = true;
          setConfigOptions((prev) => ({ ...prev, ...normalizeStateCity(data) }));
        })
        .catch((err) => {
          console.error("state/city fetch failed", err.response?.status, err.response?.data || err.message);
        });
    }
  };

  // Shared by continueLead and the refresh-restore effect below. Always asks
  // for both the lead_details AND finanicer_details tabs — if add_financier
  // was never called for this lead the tab just comes back empty/absent and
  // is a no-op, but when it *was* saved (loan_type/employment_type/selected
  // banks), step 2 needs it to actually prefill instead of showing blank.
  // Resolves to { unlockedUpTo } — the furthest step the saved data actually
  // supports opening (null on failure): step 2 while no financier is saved,
  // 4 once one is (Upload Documents has no required data of its own), and 5
  // only once the saved property/loan details pass step 4's validation.
  const loadLeadDetails = async (rawId) => {
    setLoadingLeadDetails(true);
    try {
      const { data } = await getLeadDetails(rawId, ["lead_details", "finanicer_details", "loan_details", "customer_details"]);
      if (!isApiSuccess(data)) {
        flash(data?.message || "Could not load saved details");
        return;
      }
      // lead_details (step 1) and loan_details (step 4) are both flat and
      // already match V_FIELDS' own names, so both merge the same way.
      const leadDetails = data.data?.lead_details || {};
      const loanDetails = data.data?.loan_details || {};
      setV((prev) => {
        const next = { ...prev };
        V_FIELDS.forEach((f) => {
          if (!f.name) return;
          if (leadDetails[f.name] !== undefined) next[f.name] = leadDetails[f.name];
          if (loanDetails[f.name] !== undefined) next[f.name] = loanDetails[f.name];
        });
        return next;
      });
      const finTab = data.data?.finanicer_details;
      if (finTab) {
        // The lead row defaults loan_type/employment_type to 0 until step 2 is
        // saved — 0 means "not picked", not a real option id.
        setV((prev) => ({
          ...prev,
          ...(isSavedId(finTab.loan_type) ? { loan_type: finTab.loan_type } : {}),
          ...(isSavedId(finTab.employment_type) ? { employment_type: finTab.employment_type } : {}),
        }));
        if (Array.isArray(finTab.financiers) && finTab.financiers.length) {
          setFins(Object.fromEntries(finTab.financiers.map((f) => [String(f.financier_id), true])));
          setFinancierSaved(true);
        }
      }
      // customer_details (step 5) is nested — flatten it back onto v/coborrowers.
      const customerTab = data.data?.customer_details;
      if (customerTab) {
        const { v: customerVPatch, coborrowers: coborrowersPatch } = flattenCustomerDetails(customerTab);
        if (Object.keys(customerVPatch).length) setV((prev) => ({ ...prev, ...customerVPatch }));
        if (coborrowersPatch.length) setCoborrowers(coborrowersPatch);
      }
      const financiersSaved = Array.isArray(finTab?.financiers) && finTab.financiers.length > 0;
      if (!financiersSaved) return { unlockedUpTo: STEP_NUMBER_BY_KEY.financier_selection };
      const savedValues = { ...defaultValues(), ...leadDetails, ...loanDetails };
      const propertySaved = Object.keys(loanDetails).length > 0 && !Object.keys(validateFields(PROPERTY_FIELDS, savedValues)).length;
      return {
        unlockedUpTo: propertySaved ? STEP_NUMBER_BY_KEY.customer_coborrower_details : STEP_NUMBER_BY_KEY.property_loan_details,
      };
    } catch (err) {
      flash(err.response?.data?.message || "Could not load saved details");
      return null;
    } finally {
      setLoadingLeadDetails(false);
    }
  };

  // Runs once on mount — restores the in-progress application after a refresh.
  // The URL's :leadId (present for "Complete Application") is authoritative for
  // WHICH lead; the local draft (if it matches) restores instantly without
  // waiting on the network, and also carries fields no backend step saves yet.
  useEffect(() => {
    setDraftRestored(true);
    if (!onApplicationRoute || loanId) return;
    const draft = getApplicationDraft();
    // A bare /application whose draft already has a lead id is a lead created
    // this session and then refreshed (startNew clears the draft before
    // opening a fresh /application) — resume that lead instead of dropping it.
    const resumedLeadId = !applicationLeadMatch && draft?.loanId ? Number(draft.loanId) : null;
    const urlLeadId = applicationLeadMatch?.params?.leadId ? Number(applicationLeadMatch.params.leadId) : resumedLeadId;
    const draftMatches = draft && Number(draft.loanId) === urlLeadId;
    if (resumedLeadId) navigate(`/application/${resumedLeadId}`, { replace: true });

    if (urlLeadId) {
      if (draftMatches) {
        setStep(draft.lastSavedStep || draft.step || 1);
        setLastSavedStep(draft.lastSavedStep || null);
        setV(draft.v || defaultValues());
        setCoborrowers(draft.coborrowers || []);
        setFins(draft.fins || {});
        setFinancierSaved(!!draft.financierSaved);
        setCanAddFinancier(draft.canAddFinancier !== false);
        setIsFrozen(!!draft.isFrozen);
        setDocsLocked(!!draft.docsLocked);
        setAllStepsOpen(draft.allStepsOpen ?? !!draft.isFrozen);
        setMaxStepReached(draft.maxStepReached || draft.step || 1);
      } else {
        setStep(1);
      }
      setLoanId(urlLeadId);
      loadConfigIfNeeded();
      if (!draftMatches) loadLeadDetails(urlLeadId);
    } else if (draft && !draft.loanId) {
      // A brand-new (not-yet-created) lead's in-progress step 1 draft.
      setStep(draft.step || 1);
      setV(draft.v || defaultValues());
      setCoborrowers(draft.coborrowers || []);
      setFins(draft.fins || {});
      loadConfigIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- lead source → lead source name (partner) dependent dropdown ----
  useEffect(() => {
    if (!v.lead_source_type) {
      setConfigOptions((c) => (c.partner_list ? { ...c, partner_list: [] } : c));
      return;
    }
    fetchPartnerList(v.lead_source_type)
      .then(({ data }) => {
        if (!isApiSuccess(data)) {
          console.error("partner list fetch failed", data?.message);
          return;
        }
        setConfigOptions((c) => ({ ...c, partner_list: Array.isArray(data.data) ? data.data : [] }));
      })
      .catch((err) => {
        console.error("partner list fetch failed", err.response?.status, err.response?.data || err.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.lead_source_type]);

  const toBankShape = (f) => ({ key: String(f.id), name: financierDisplayName(f), logo: f.logo || "", code: f.financial_code || "" });
  const selectedBanksList = useMemo(
    () => (configOptions.financier_list || []).filter((f) => fins[String(f.id)]).map(toBankShape),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configOptions.financier_list, fins]
  );
  const financierList = () => configOptions.financier_list || [];
  const selectedBanks = () => selectedBanksList;

  // Editing a field clears its own error; the full check runs again on save.
  const setField = (k, val) => {
    setV((prev) => ({ ...prev, [k]: val }));
    setFieldErrors((e) => {
      if (!e[k]) return e;
      const { [k]: _gone, ...rest } = e;
      return rest;
    });
    touchSaved();
  };

  const onCoborrowerFieldChange = (index, k, val) => {
    setCoborrowers((prev) => prev.map((cb, i) => (i === index ? { ...cb, [k]: val } : cb)));
    setCoFieldErrors((list) => {
      if (!list[index] || !list[index][k]) return list;
      const { [k]: _gone, ...rest } = list[index];
      return list.map((errs, i) => (i === index ? rest : errs));
    });
    touchSaved();
  };

  const startNew = () => {
    clearApplicationDraft();
    setStep(1);
    setLoanId(null);
    setV(defaultValues());
    setCoborrowers([]);
    setFins({});
    setFinancierSaved(false);
    setCanAddFinancier(true);
    setIsFrozen(false);
    setDocsLocked(false);
    setAllStepsOpen(false);
    setMaxStepReached(1);
    setLastSavedStep(null);
    setSavedAt("All changes saved");
    loadConfigIfNeeded();
    navigate("/application");
  };

  // lead carries the dashboard's per-lead flags (get_lead_details doesn't
  // expose them) — last_form_filled lands the user directly on the step they
  // last saved instead of always restarting at step 1.
  // viewOnly (a tap on the dashboard card itself, not its CTA): read-only on
  // every tab, all five open, starting from the first.
  const continueLead = async (lead, { viewOnly = false } = {}) => {
    const landingStep = viewOnly ? 1 : (lead.lastFormFilled && STEP_NUMBER_BY_KEY[lead.lastFormFilled]) || 1;
    setStep(landingStep);
    setLastSavedStep(lead.lastFormFilled ? landingStep : null);
    setLoanId(lead.rawId);
    setV({ ...defaultValues(), name: lead.name });
    setCoborrowers([]);
    setFins({});
    setFinancierSaved(false);
    setCanAddFinancier(lead.enableAddFinancier !== false);
    // Not the viewer's own lead (e.g. a reportee's) — always view-only,
    // regardless of is_freeze.
    const frozen = viewOnly || !!lead.isFreeze || lead.isSameUser === false;
    setIsFrozen(frozen);
    setDocsLocked(!!lead.isFreeze || lead.isSameUser === false);
    // is_complete_application_cta means the application isn't created yet.
    setAllStepsOpen(viewOnly || (frozen && (!!lead.isFreeze || !lead.isCompleteApplicationCta)));
    // Provisional until get_lead_details comes back: steps up to
    // last_form_filled are the most that could be open.
    setMaxStepReached(landingStep);
    loadConfigIfNeeded();
    navigate(`/application/${lead.rawId}`);
    const saved = await loadLeadDetails(lead.rawId);
    // View-only leads can't save, so nothing past last_form_filled ever opens
    // for them (and a created one has every tab open anyway).
    if (!saved || frozen) return;
    // last_form_filled alone isn't trusted to unlock tabs — it can run ahead
    // of what's actually saved (e.g. a partial autosave). The saved step and
    // the one after it open, but never past what get_lead_details' data
    // supports; anything further stays locked until its previous tab is saved.
    const unlocked = Math.min(landingStep + 1, LEAD_FORM_STEPS.length, saved.unlockedUpTo);
    setMaxStepReached(unlocked);
    setStep((s) => Math.min(s, unlocked));
  };

  // "Add bank" on the Send to Bank page: add_financier with save_type "update"
  // and only the newly added financiers (unlike step 2's "auto", which sends
  // the full list plus loan_type / employment_type). The lead stays within
  // MAX_FINANCIERS in total. Resolves true once saved.
  const addFinanciersToLead = async (newKeys) => {
    const current = selectedBanks().map((b) => b.key);
    const added = newKeys.filter((k) => !current.includes(k));
    if (!added.length) return false;
    if (current.length + added.length > MAX_FINANCIERS) {
      flash("A lead can have up to " + MAX_FINANCIERS + " banks");
      return false;
    }
    setSavingFinancier(true);
    try {
      const { data } = await addFinancier({
        lead_id: Number(loanId),
        save_type: "update",
        financier: added.map((k) => ({ financier_id: Number(k) })),
      });
      if (!isApiSuccess(data)) {
        flash(data?.message || "Could not add bank");
        return false;
      }
      setFins((f) => ({ ...f, ...Object.fromEntries(added.map((k) => [k, true])) }));
      setFinancierSaved(true);
      touchSaved();
      flash(data?.message || (added.length === 1 ? "Bank added" : added.length + " banks added"));
      return true;
    } catch (err) {
      flash(err.response?.data?.message || "Could not add bank");
      return false;
    } finally {
      setSavingFinancier(false);
    }
  };

  const goStep = (n) => {
    if (!loanId && n > 1) {
      flash("Create the lead first");
      return;
    }
    // allStepsOpen (a view-only lead whose application is already created)
    // means every tab's data exists, so any tab can be opened. Otherwise only
    // the active step or an already-submitted earlier one is reachable; a step
    // ahead has no data yet to show.
    if (!allStepsOpen && n > maxStepReached) {
      flash("Please complete the current step first");
      return;
    }
    setStep(n);
    window.scrollTo(0, 0);
  };

  // Customer Details keeps its sections collapsed, so a failed "Next" opens
  // the borrower section holding the first error (only one is open at a time)
  // and, per co-borrower, the first of its sections with an error — then
  // scrolls to the first error message once it has rendered.
  const closeFinConfirm = () => setFinConfirm(false);
  const confirmFinancierSave = () => {
    setFinConfirm(false);
    next({ financierConfirmed: true });
  };

  const openCustomerErrorSections = (errs, coErrs) => {
    const hasErr = (block, e) => block.fields.some((f) => f.name && e?.[f.name]);
    const mainBlock = CUSTOMER_ACCORDION_BLOCKS.find((b) => hasErr(b, errs));
    if (mainBlock) setOpenCust(mainBlock.key);
    const cbOpen = {};
    coErrs.forEach((e, index) => {
      const cbBlock = CO_BLOCKS.find((b) => hasErr(b, e));
      if (cbBlock) cbOpen[index] = cbBlock.key;
    });
    if (Object.keys(cbOpen).length) setOpenCb((prev) => ({ ...prev, ...cbOpen }));
    setTimeout(() => {
      const el = document.querySelector(".field-error");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const next = async ({ financierConfirmed = false } = {}) => {
    // is_freeze: everything on this lead is already done — pure view mode,
    // just move between steps with no validation/save calls at all.
    if (isFrozen) {
      // Last tab, or the last one a not-yet-created view-only lead has filled.
      if (step === 5 || (!allStepsOpen && step >= maxStepReached)) {
        navigate("/dashboard");
        return;
      }
      setStep((s) => s + 1);
      window.scrollTo(0, 0);
      return;
    }
    let errs = {};
    let coErrs = []; // one error-object per co-borrower, same order as coborrowers
    // Lead Details / Financier selection are read-only once already saved —
    // nothing to validate or re-save, the button just advances to the next step.
    if (step === 1 && !loanId) errs = validateFields(LEAD_STEP.blocks[0].fields, v, configOptions);
    else if (step === 2 && !financierSaved) errs = validateFields(FIN_STEP.blocks[0].fields, v, configOptions);
    else if (step === 4) errs = validateFields(PROPERTY_FIELDS, v, configOptions);
    else if (step === 5) {
      errs = validateFields(CUSTOMER_MAIN_FIELDS, v, configOptions);
      if (v.has_coborrower === "yes") {
        coErrs = coborrowers.map((cb) => validateFields(CO_FIELDS, cb, configOptions));
      }
    }
    const hasCoErrs = coErrs.some((e) => Object.keys(e).length > 0);
    if (Object.keys(errs).length || hasCoErrs) {
      setFieldErrors(errs);
      setCoFieldErrors(coErrs);
      if (step === 5) openCustomerErrorSections(errs, coErrs);
      flash("Please fix the highlighted fields");
      return;
    }
    setFieldErrors({});
    setCoFieldErrors([]);

    if (step === 1) {
      if (loanId) {
        // Editing an existing lead — Lead Details is read-only, just advance.
        setStep(2);
        bumpMaxStep(2);
        touchSaved();
        window.scrollTo(0, 0);
        return;
      }
      setSavingLead(true);
      try {
        const payload = buildFieldPayload(LEAD_STEP.blocks[0].fields, v);
        const { data } = await createProspect(payload);
        if (!isApiSuccess(data)) {
          flash(data?.message || "Could not create lead");
          return;
        }
        const createdId = extractProspectId(data, loanId);
        setLoanId(createdId);
        setLastSavedStep(1);
        // Put the new lead's id in the URL so a refresh restores this lead
        // (the restore effect keys off /application/:leadId).
        if (createdId) navigate(`/application/${createdId}`, { replace: true });
        setLeadModal(true);
        touchSaved();
      } catch (err) {
        flash(err.response?.data?.message || "Could not create lead");
      } finally {
        setSavingLead(false);
      }
      return;
    }
    if (step === 2) {
      if (financierSaved) {
        // Saved once, step 2 is read-only (banks are added later from the
        // Send to Bank page) — Next just advances, no second add_financier.
        setStep(3);
        bumpMaxStep(3);
        touchSaved();
        window.scrollTo(0, 0);
        return;
      }
      if (!selectedBanks().length) {
        flash("Please select at least one bank");
        return;
      }
      if (!financierConfirmed) {
        setFinConfirm(true);
        return;
      }
      setSavingFinancier(true);
      try {
        // AddFinancierDto: save_type "auto" (requires loan_type +
        // employment_type — the step-2 fields), lead_id, and financier as
        // [{ financier_id }]. Unknown keys are rejected by the ValidationPipe.
        // Step 2 always saves with save_type "auto" (loan_type + employment_type
        // + the selected banks).
        const payload = {
          ...buildFieldPayload(FIN_STEP.blocks[0].fields, v),
          save_type: "auto",
          lead_id: Number(loanId),
          financier: selectedBanks().map((b) => ({ financier_id: Number(b.key) })),
          last_form_filled: "financier_selection",
        };
        const { data } = await addFinancier(payload);
        if (!isApiSuccess(data)) {
          flash(data?.message || "Could not save financier selection");
          return;
        }
        setFinancierSaved(true);
        setLastSavedStep(2);
      } catch (err) {
        flash(err.response?.data?.message || "Could not save financier selection");
        return;
      } finally {
        setSavingFinancier(false);
      }
    }
    if (step === 4) {
      setSavingProperty(true);
      try {
        const payload = buildFieldPayload(PROPERTY_FIELDS, v);
        const { data } = await saveLeadData(loanId, "property_loan_details", payload);
        if (!isApiSuccess(data)) {
          flash(data?.message || "Could not save property/loan details");
          return;
        }
        setLastSavedStep(4);
      } catch (err) {
        flash(err.response?.data?.message || "Could not save property/loan details");
        return;
      } finally {
        setSavingProperty(false);
      }
    }
    if (step === 5) {
      setSavingCustomer(true);
      try {
        const details = buildCustomerCoborrowerDetails(v, coborrowers);
        const { data } = await saveLeadData(loanId, "customer_coborrower_details", details);
        if (!isApiSuccess(data)) {
          flash(data?.message || "Could not save customer details");
          return;
        }
      } catch (err) {
        flash(err.response?.data?.message || "Could not save customer details");
        return;
      } finally {
        setSavingCustomer(false);
      }
      clearApplicationDraft();
      navigate("/application/created");
      return;
    }
    setStep((s) => s + 1);
    bumpMaxStep(step + 1);
    touchSaved();
    window.scrollTo(0, 0);
  };

  const back = () => {
    if (step === 1) {
      clearApplicationDraft();
      navigate("/dashboard");
      return;
    }
    setStep((s) => s - 1);
    window.scrollTo(0, 0);
  };

  const closeLeadModal = () => {
    setLeadModal(false);
    setStep(2);
    bumpMaxStep(2);
    window.scrollTo(0, 0);
  };

  const displayLoanId = formatLoanCode(loanId);

  const copyLoanId = () => {
    const id = displayLoanId || "REM12344678";
    try {
      navigator.clipboard && navigator.clipboard.writeText(id);
    } catch (e) {
      /* clipboard unavailable */
    }
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1400);
  };

  const copyBorrower = (index) => {
    setCoborrowers((prev) =>
      prev.map((cb, i) =>
        i === index ? { ...cb, pa_address: v.pa_address, pa_province: v.pa_province, pa_city: v.pa_city, pa_zipcode: v.pa_zipcode } : cb
      )
    );
    setOpenCb((prev) => ({ ...prev, [index]: "address" }));
    touchSaved();
    flash("Borrower address copied");
  };

  const activeName = v.name || "New applicant";

  return {
    step,
    setStep,
    v,
    setV,
    coborrowers,
    configOptions,
    savingLead,
    savingFinancier,
    savingProperty,
    savingCustomer,
    loadingLeadDetails,
    fieldErrors,
    coFieldErrors,
    fins,
    setFins,
    financierSaved,
    canAddFinancier,
    addFinanciersToLead,
    finConfirm,
    closeFinConfirm,
    confirmFinancierSave,
    isFrozen,
    docsLocked,
    allStepsOpen,
    maxStepReached,
    loanId,
    setLoanId,
    loadLeadDetails,
    leadModal,
    setLeadModal,
    copied,
    openCust,
    setOpenCust,
    openAcc,
    setOpenAcc,
    openCb,
    setOpenCb,
    loadConfigIfNeeded,
    startNew,
    continueLead,
    goStep,
    next,
    back,
    closeLeadModal,
    displayLoanId,
    copyLoanId,
    copyBorrower,
    setField,
    onCoborrowerFieldChange,
    financierList,
    selectedBanks,
    toBankShape,
    activeName,
  };
}
